import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useQuery } from '@tanstack/react-query';
import BreadcrumbNav from '../components/cloud/BreadcrumbNav';
import FileUploader from '../components/cloud/FileUploader';
import { Folder, File, Upload } from 'lucide-react';
import { formatFileSize } from '../utils/format';
import { FileObject as SupabaseFileObject } from '@supabase/storage-js';

interface FileObject extends Partial<SupabaseFileObject> {
  id: string;
  name: string;
  type: string;
  size: number;
  path: string;
  metadata: {
    size: number;
    mimetype?: string;
  }
}

type FileItem = FileObject;

function convertSupabaseFile(file: SupabaseFileObject): FileObject {
  return {
    id: file.id || Math.random().toString(36).substr(2, 9),
    name: file.name,
    type: file.metadata?.mimetype || 'application/octet-stream',
    size: file.metadata?.size || 0,
    path: file.name,
    metadata: {
      size: file.metadata?.size || 0,
      mimetype: file.metadata?.mimetype
    }
  };
}

export default function Cloud() {
  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [showUploader, setShowUploader] = useState(false);

  const { data: files = [], isLoading } = useQuery({
    queryKey: ['files', currentPath],
    queryFn: async () => {
      const path = currentPath.join('/');
      const { data, error } = await supabase
        .storage
        .from('files')
        .list(path);

      if (error) throw error;
      return data.map((file: SupabaseFileObject) => convertSupabaseFile(file));
    }
  });

  const handleUploadFile = async (file: File) => {
    const path = currentPath.join('/');
    const { error } = await supabase
      .storage
      .from('files')
      .upload(`${path}/${file.name}`, file);

    if (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const handleNavigate = (path: string[]) => {
    setCurrentPath(path);
  };

  const formatSize = (item: FileItem) => {
    return formatFileSize(item.metadata?.size || item.size || 0)
  }

  if (isLoading) {
    return <div>加载中...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <BreadcrumbNav 
          currentPath={currentPath}
          onNavigate={handleNavigate}
        />
        <button
          onClick={() => setShowUploader(true)}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Upload className="w-4 h-4" />
          <span>上传文件</span>
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {files.map((item) => (
          <div
            key={item.id}
            className="p-4 border rounded-lg hover:shadow-md transition-shadow"
          >
            <div className="flex items-center space-x-3">
              {item.metadata?.mimetype?.startsWith('image/') ? (
                <img
                  src={`${process.env.VITE_SUPABASE_URL}/storage/v1/object/public/files/${currentPath.join('/')}/${item.name}`}
                  alt={item.name}
                  className="w-8 h-8 object-cover rounded"
                />
              ) : (
                <File className="w-8 h-8 text-gray-400" />
              )}
              <div>
                <div className="font-medium">{item.name}</div>
                <div className="text-sm text-gray-500">
                  {formatSize(item)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showUploader && (
        <FileUploader 
          onUploadFile={handleUploadFile}
          onClose={() => setShowUploader(false)}
        />
      )}
    </div>
  );
}